import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { mergePages } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import basePages from './sitemap-pages.json';
import pageOverrides from '../pages.json';
import { isSisterPropertiesPageActive } from '../hooks/useHasSisterProperties';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface FaqItem {
  question?: string | null;
  answer?: string | null;
}

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Mirror Header.tsx: drop the FAQ page from the visible sitemap when the
  // property has no published FAQ entries. Keeps the sitemap page in sync
  // with the top-nav — both surfaces hide together.
  const faqs = useCollectionBinding<FaqItem>('sitemapcontent.faqs', { collection: 'faqs' });
  const hasFaqs = (faqs || []).some(
    (f) =>
      (f.question && String(f.question).trim()) ||
      (f.answer && String(f.answer).trim()),
  );

  // Mirror Header.tsx: Sister Properties appears when the page is active
  // (visible + section present), not only after community cards are filled.
  const sisterPropertiesActive = isSisterPropertiesPageActive();

  const pages = useMemo(
    () =>
      mergePages(basePages as any, pageOverrides as any).filter((p) => {
        if (p.visible === false) return false;
        // Apply Now is a header utility CTA (NAV_EXCLUDED_ALIASES), not a
        // sitemap listing — same treatment as thank-you / the sitemap page itself.
        if (
          p.path === '/sitemap' ||
          p.path === '/thank-you' ||
          p.path === '/apply-now' ||
          p.path.includes('{{')
        ) {
          return false;
        }
        if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
        if (p.alias === 'faq' && !hasFaqs) return false;
        if (p.alias === 'sister-properties' && !sisterPropertiesActive) return false;
        return true;
      }),
    [hasVirtualTours, hasFaqs, sisterPropertiesActive],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-[var(--vibe-color-text)] rounded px-6 py-4 text-center font-body text-sm text-foreground hover:bg-[var(--vibe-color-secondary)] hover:text-[var(--vibe-color-text-on-secondary)] hover:border-[var(--vibe-color-secondary)] transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'sitemapcontent.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
